import { http, httpPaged, type PagedResponse } from '@/services/http';

export interface Form {
  id: number;
  name: string;
  description?: string;
  sku?: string;
  version?: number;
  published?: boolean;
  image?: string;
  sections?: Array<{ controls?: unknown[] }>;
  [key: string]: unknown;
}

function projectScope(projectId: number | string): string {
  return `projects/${projectId}/`;
}

export const formsApi = {
  index(projectId: number | string): Promise<Form[]> {
    return http<Form[]>({ url: `${projectScope(projectId)}forms`, method: 'get' });
  },

  indexPaged(
    projectId: number | string,
    page: number,
    per: number,
  ): Promise<PagedResponse<Form[]>> {
    return httpPaged<Form[]>({
      url: `${projectScope(projectId)}forms`,
      method: 'get',
      params: { page, per },
    });
  },

  search(projectId: number | string, query: string): Promise<Form[]> {
    return http<Form[]>({
      url: `${projectScope(projectId)}forms/search`,
      method: 'get',
      params: { query },
    });
  },

  indexWithFields(projectId: number | string): Promise<Form[]> {
    return http<Form[]>({ url: `${projectScope(projectId)}forms_with_fields`, method: 'get' });
  },

  get(projectId: number | string, formId: number | string): Promise<Form> {
    return http<Form>({ url: `${projectScope(projectId)}forms/${formId}`, method: 'get' });
  },

  create(projectId: number | string, form: Partial<Form>): Promise<Form> {
    const body = { ...form, version: form.version ?? 1 };
    return http<Form>({ url: `${projectScope(projectId)}forms`, method: 'post', data: body });
  },

  clone(projectId: number | string, formId: number | string): Promise<Form> {
    return http<Form>({
      url: `${projectScope(projectId)}forms/${formId}/clone`,
      method: 'post',
    });
  },

  update(projectId: number | string, form: Partial<Form> & { id: number }): Promise<Form> {
    return http<Form>({
      url: `${projectScope(projectId)}forms/${form.id}`,
      method: 'patch',
      data: form,
    });
  },

  remove(projectId: number | string, formId: number | string): Promise<void> {
    return http<void>({ url: `${projectScope(projectId)}forms/${formId}`, method: 'delete' });
  },

  publish(projectId: number | string, formId: number | string): Promise<Form> {
    return http<Form>({
      url: `${projectScope(projectId)}forms/${formId}/publish`,
      method: 'patch',
    });
  },

  /** Legacy validation rule: form must have ≥1 section and ≥1 control. */
  async validate(projectId: number | string, formId: number | string): Promise<boolean> {
    const form = await formsApi.get(projectId, formId);
    const sections = form.sections ?? [];
    if (sections.length === 0) return false;
    const first = sections[0];
    return (first?.controls?.length ?? 0) > 0;
  },

  newBlank(): Form {
    return { id: 0, name: '', description: '', sku: '' };
  },
};
